import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useParams } from "react-router-dom";

import Alert from "veneer/Alert";
import DataTable, {
  createColumnHelper,
  createFilter,
  sorting,
} from "veneer/DataTable";

import { devicesUptimeReportSelector } from "cortex/ui/src/fleet/redux-store/ui-state/report-uptime-devices/selectors";
import { useAppDispatch, useAppSelector } from "cortex/ui/src/hooks";
import { Network } from "cortex/ui/src/network/model";
import {
  deviceStoresLoadedSelector,
  devicesSelector,
} from "cortex/ui/src/redux-store/devices";
import {
  networksSelector,
  networksStoresLoadedSelector,
} from "cortex/ui/src/redux-store/networks";
import { NavLoader } from "cortex/ui/src/ui/loader";
import Notification from "cortex/ui/src/ui/notification";
import { formatPercentage } from "cortex/ui/src/ui/table/formats";
import { getErrorMessage } from "cortex/ui/src/util/response";
import { Container, Panel } from "cortex/ui/src/view/layout";

import ReportFilter from "./ReportFilter";
import {
  type FetchUptimeDevicesReportArgs,
  fetchUptimeDevicesReport,
  unloadUptimeDevicesReport,
} from "../action";
import { UptimeDevicesRow } from "../model";

export default function UptimeDevicesReport() {
  const { date, end, networkId, start } = useParams<{
    date?: string;
    end?: string;
    networkId?: string;
    start?: string;
  }>();
  const dispatch = useAppDispatch();
  const devices = useAppSelector(devicesSelector);
  const networks = useAppSelector(networksSelector);
  const networksLoaded = useAppSelector(networksStoresLoadedSelector);
  const devicesLoaded = useAppSelector(deviceStoresLoadedSelector);
  const storesLoaded = networksLoaded && devicesLoaded;
  const report = useAppSelector(devicesUptimeReportSelector);

  const network: Network | undefined = useMemo(
    () => (networkId ? networks?.[networkId] : undefined),
    [networks, networkId],
  );

  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const isMountedRef = useRef(false);

  useEffect(() => {
    if (!network || !date) {
      unloadUptimeDevicesReport(dispatch);
    }
  }, [dispatch, network, date]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      unloadUptimeDevicesReport(dispatch);
    };
  }, [dispatch]);

  useEffect(() => {
    const canFetch = Boolean(network && date);
    if (!canFetch) return;

    const args: FetchUptimeDevicesReportArgs = {
      date: date!,
      networkId: network!.id,
    };

    setSubmitted(true);

    fetchUptimeDevicesReport(dispatch, args)
      .catch((error) => {
        const msg = getErrorMessage(
          error,
          "Failed to fetch uptime devices report.",
        );
        setErrorMessage(msg);
      })
      .finally(() => setSubmitted(false));
  }, [date, dispatch, network]);

  const exportFileName = useCallback(
    (prefix: string) => {
      const name = network ? network.name : "";
      return `${prefix}_${name}_${date}.csv`;
    },
    [date, network],
  );

  const downloadCsv = useCallback(
    (rows: ReadonlyArray<UptimeDevicesRow>) => {
      const header = [["Device ID", "Name", "Uptime", "Current Network"]];
      const lines = rows.map((row) => {
        return [
          row.deviceId,
          row.name || "",
          formatPercentage(row.uptime),
          row.networkName || "",
        ];
      });

      const escaped = [header, lines]
        .flat()
        .map((arr) =>
          arr.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","),
        )
        .join("\n");

      const blob = new Blob([escaped], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = exportFileName("network-uptime-date");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    [exportFileName],
  );

  const renderDevicesUptimeReport = useCallback(() => {
    if (!report || Object.keys(report).length === 0) {
      return <p>No data to display.</p>;
    }
    const data: ReadonlyArray<UptimeDevicesRow> = Object.keys(report).map(
      (d) => ({
        deviceId: devices[Number(d)]?.device_id,
        id: Number(d),
        name: devices[Number(d)]?.name,
        networkId: network?.id,
        networkName: network?.name,
        uptime: report[d],
      }),
    );

    const filter = createFilter<UptimeDevicesRow>(
      "deviceId",
      "name",
      "networkName",
    );
    const columnHelper = createColumnHelper<UptimeDevicesRow>();

    const columns = [
      columnHelper.accessor("deviceId", {
        header: "Device ID",
        cell: (prop) => {
          const row = prop.row.original;
          if (!network) {
            return row.deviceId;
          }
          const url = `/diagnostics/uptime-report/${network.id}/${start}/${end}/device/${row.id}`;
          return <Link to={url}>{row.deviceId}</Link>;
        },
        sortingFn: sorting.string((row) => row.deviceId),
      }),
      columnHelper.accessor("name", {
        header: "Name",
        cell: (prop) => prop.getValue() || "-",
        sortingFn: sorting.string((row) => row.name || ""),
      }),
      columnHelper.accessor("uptime", {
        header: "Uptime",
        cell: (prop) => formatPercentage(prop.getValue()),
        sortingFn: sorting.number((row) => row.uptime),
      }),
      columnHelper.accessor("networkId", {
        header: "Current Network",
        cell: (prop) => {
          const row = prop.row.original;
          return (
            <Link to={`/networks/${row.networkId}`}>{row.networkName}</Link>
          );
        },
        sortingFn: sorting.string((row) => row.networkName || ""),
      }),
    ];

    return (
      <DataTable
        columns={columns}
        filter={filter}
        initialPageSize={10}
        initialSort={{ id: "deviceId", desc: false }}
        onExport={(rows) => downloadCsv(rows)}
        rows={data}
      />
    );
  }, [devices, downloadCsv, end, network, report, start]);

  const title = network
    ? `Network Uptime: ${network.name} ${date}`
    : "Network Uptime";

  if (!storesLoaded) {
    return (
      <Container title="Uptime Report">
        <NavLoader.Proxy />
      </Container>
    );
  }

  return (
    <Container hideBox title="Uptime Report">
      {errorMessage && <Notification error={errorMessage} />}
      <ReportFilter
        end={end}
        network={network}
        start={start}
        submitted={submitted}
      />
      <Panel borderRadius={2} spacing={2} title={title}>
        {renderDevicesUptimeReport()}
        <Alert severity="info">
          {`These devices were part of the ${network?.name ?? ""} on ${date}`}.
        </Alert>
      </Panel>
    </Container>
  );
}
